import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { Spinner } from "./spinner"
import type { SpinnerSize } from "./spinner"

// Map the legacy button-size vocabulary to Spinner size tokens
const btnSizeToSpinner: Record<"sm" | "default" | "lg", SpinnerSize> = {
  sm: "xs",
  default: "sm",
  lg: "md",
}

// Backward-compat alias so any external import of LoadingSpinner still works
const LoadingSpinner = ({ className, size = "default" }: { className?: string; size?: "sm" | "default" | "lg" }) => (
  <Spinner size={btnSizeToSpinner[size]} className={cn("text-inherit", className)} />
)

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold transition-all duration-200 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring/30 cursor-pointer",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 active:bg-primary",
        // Metronic Design System
        primary:
          "bg-primary text-white shadow-sm hover:bg-primary/90 active:bg-primary",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-accent active:bg-accent",
        tertiary:
          "bg-transparent text-foreground border border-border hover:bg-secondary active:bg-accent",
        destructive:
          "bg-danger text-white shadow-sm hover:bg-danger/90 active:bg-danger",
        outline:
          "border border-border bg-card hover:bg-secondary hover:text-foreground",
        ghost:
          "hover:bg-secondary hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Metronic Light variants
        "primary-light":
          "bg-primary-light text-primary hover:bg-primary hover:text-white",
        "success":
          "bg-success text-white shadow-sm hover:bg-success/90",
        "success-light":
          "bg-success-light text-success hover:bg-success hover:text-white",
        "warning":
          "bg-warning text-white shadow-sm hover:bg-warning/90",
        "warning-light":
          "bg-warning-light text-warning hover:bg-warning hover:text-white",
        "info":
          "bg-info text-white shadow-sm hover:bg-info/90",
        "info-light":
          "bg-info-light text-info hover:bg-info hover:text-white",
        "danger-light":
          "bg-danger-light text-danger hover:bg-danger hover:text-white",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md gap-1.5 px-3 text-xs",
        lg: "h-10 lg:h-9 rounded-md px-6",
        "lg-full": "h-10 lg:h-9 w-full rounded-md px-6",
        icon: "size-9",
        "icon-sm": "size-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  loadingText,
  children,
  disabled,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    loading?: boolean
    loadingText?: string
  }) {
  const Comp = asChild ? Slot : "button"
  
  // Determine spinner size based on button size
  const spinnerSize: "sm" | "default" | "lg" = size === "sm" ? "sm" : size === "lg" ? "lg" : "default"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2">
          <LoadingSpinner size={spinnerSize} />
          {loadingText ? <span>{loadingText}</span> : null}
        </span>
      ) : (
        children
      )}
    </Comp>
  )
}

export { Button, buttonVariants, LoadingSpinner }
